import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface XpState {
  totalXp: number;
  contextXp: number;
  contextLabel: string;
  contextId: string | null;
  delta: { amount: number; key: number } | null;
}

interface XpContextValue extends XpState {
  setTotalXp: (xp: number) => void;
  setContext: (label: string, id: string | null, xp: number) => void;
  pushDelta: (amount: number) => void;
  setContextXp: (xp: number) => void;
}

const XpContext = createContext<XpContextValue | null>(null);

export function XpProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<XpState>({
    totalXp: 0,
    contextXp: 0,
    contextLabel: '',
    contextId: null,
    delta: null,
  });

  const setTotalXp = useCallback((xp: number) => {
    setState(s => ({ ...s, totalXp: xp }));
  }, []);

  const setContext = useCallback((label: string, id: string | null, xp: number) => {
    setState(s => ({ ...s, contextLabel: label, contextId: id, contextXp: xp }));
  }, []);

  const setContextXp = useCallback((xp: number) => {
    setState(s => ({ ...s, contextXp: xp }));
  }, []);

  const pushDelta = useCallback((amount: number) => {
    if (amount === 0) return;
    setState(s => ({ ...s, delta: { amount, key: Date.now() } }));
  }, []);

  return (
    <XpContext.Provider value={{ ...state, setTotalXp, setContext, pushDelta, setContextXp }}>
      {children}
    </XpContext.Provider>
  );
}

export function useXp() {
  const ctx = useContext(XpContext);
  if (!ctx) throw new Error('useXp must be inside XpProvider');
  return ctx;
}
